import { queryOptions, useMutation } from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch, apiUpload } from "@/lib/api/client"
import { pollUnlessLive } from "@/lib/api/realtime"
import { toEvidence } from "@/queries/evidence"
import type { ApiResource } from "@/lib/api/types"
import type { DocumentReading, ApiDocumentReading } from "@/types/document"
import type { IdentityDocument, ApiIdentityDocument } from "@/types/individual"

const checkLabels: Record<string, string> = {
  "document.readable": "Legible",
  "document.form": "The right kind of document",
  "document.integrity": "Machine-readable zone",
  "document.expiry": "In date",
  "document.holder": "The same person as the record",
}

export function toDocumentReading(data: ApiDocumentReading): DocumentReading {
  const checks = data.checks.map((check) => ({
    key: check.key,
    label: checkLabels[check.key] ?? check.key,
    outcome: check.outcome,
    summary: check.summary,
    detail: null,
  }))

  return {
    id: data.id,
    evidenceId: data.evidence_id,
    status: data.status,
    outcome: data.outcome,
    verdict: data.verdict,
    failure: data.failure,
    proposed: {
      reference: data.proposed?.reference ?? null,
      issuingCountry: data.proposed?.issuing_country ?? null,
      issuedOn: data.proposed?.issued_on ?? null,
      expiresOn: data.proposed?.expires_on ?? null,
      surname: data.proposed?.surname ?? null,
      givenNames: data.proposed?.given_names ?? null,
      dateOfBirth: data.proposed?.date_of_birth ?? null,
    },
    checks,
    createdAt: data.created_at,
  }
}

export function toIdentityDocument(
  data: ApiIdentityDocument
): IdentityDocument {
  return {
    id: data.id,
    form: data.form,
    formLabel: data.form_label,
    photographic: data.photographic,
    referenceMasked: data.reference_masked,
    issuingCountry: data.issuing_country,
    issuedOn: data.issued_on,
    expiresOn: data.expires_on,
    expired: data.expired,
    current: data.current,
    file: data.file ? toEvidence(data.file) : null,
    reading: data.reading ? toDocumentReading(data.reading) : null,
    analysis: data.analysis ?? null,
    verifiedAt: data.verified_at,
    verifiedById: data.verified_by_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

function path(individualId: string, documentId: string): string {
  return `/individuals/${individualId}/identity-documents/${documentId}`
}

export const readingQueries = {
  latest: (individualId: string, documentId: string) =>
    queryOptions({
      queryKey: apiKey(`${path(individualId, documentId)}/readings/latest`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiDocumentReading>>(
          `${path(individualId, documentId)}/readings/latest`,
          { signal }
        )

        return toDocumentReading(data)
      },
      refetchInterval: (query) =>
        query.state.data?.status === "running" ? pollUnlessLive(1200) : false,
      retry: false,
    }),
}

export function useAddIdentityDocument(individualId: string) {
  return useMutation({
    mutationFn: async (values: { form: string }) => {
      const { data } = await apiFetch<ApiResource<ApiIdentityDocument>>(
        `/individuals/${individualId}/identity-documents`,
        { method: "POST", body: values }
      )

      return toIdentityDocument(data)
    },
  })
}

export function useUploadIdentityDocumentFile(individualId: string) {
  return useMutation({
    mutationFn: async ({
      documentId,
      file,
      onProgress,
    }: {
      documentId: string
      file: File
      onProgress?: (progress: number) => void
    }) => {
      const body = new FormData()
      body.append("file", file)
      const { data } = await apiUpload<ApiResource<ApiIdentityDocument>>(
        `${path(individualId, documentId)}/file`,
        { body, onProgress }
      )

      return toIdentityDocument(data)
    },
  })
}

export function useConfirmIdentityDocument(individualId: string) {
  return useMutation({
    mutationFn: async ({
      documentId,
      values,
      replaces = null,
    }: {
      documentId: string
      values: {
        reference: string | null
        issuingCountry: string | null
        issuedOn: string | null
        expiresOn: string | null
      }
      replaces?: string | null
    }) => {
      const { data } = await apiFetch<ApiResource<ApiIdentityDocument>>(
        `${path(individualId, documentId)}/confirm`,
        {
          method: "POST",
          body: {
            reference: values.reference,
            issuing_country: values.issuingCountry,
            issued_on: values.issuedOn,
            expires_on: values.expiresOn,
            replaces,
          },
        }
      )

      return toIdentityDocument(data)
    },
  })
}

export function useRevealDocumentReference(individualId: string) {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const { data } = await apiFetch<{ data: { reference: string } }>(
        `${path(individualId, documentId)}/reference`
      )

      return data.reference
    },
  })
}
